import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Banknote, ArrowRight } from "lucide-react";
import { tr } from "@/lib/i18n";

const T = {
  nl: { save: "Mogelijk recht op tot", with: "via", learn: "Bekijk alle regelingen", hint: "Mogelijk recht op subsidie", generic_title: "Mogelijk recht op subsidie", generic_body: "Bekijk regelingen voor jouw land en sector (indicatie, geen korting)" },
  en: { save: "Possibly eligible for up to", with: "via", learn: "View all schemes", hint: "Possible subsidy", generic_title: "Possible grant available", generic_body: "Explore schemes for your country and sector (indicative, not a discount)" },
  de: { save: "Möglicherweise bis zu", with: "über", learn: "Alle Programme ansehen", hint: "Mögliche Förderung", generic_title: "Mögliche Förderung verfügbar", generic_body: "Programme für Ihr Land und Ihre Branche ansehen (Richtwert, kein Rabatt)" },
  fr: { save: "Possiblement jusqu'à", with: "via", learn: "Voir tous les dispositifs", hint: "Subvention possible", generic_title: "Subvention potentielle disponible", generic_body: "Découvrez les dispositifs pour votre pays et secteur (indicatif, pas une remise)" },
  es: { save: "Posiblemente hasta", with: "vía", learn: "Ver todos los programas", hint: "Posible subvención", generic_title: "Posible subvención disponible", generic_body: "Explore programas para su país y sector (indicativo, no un descuento)" },
  it: { save: "Possibilmente fino a", with: "tramite", learn: "Vedi tutti i programmi", hint: "Possibile sovvenzione", generic_title: "Possibile sovvenzione disponibile", generic_body: "Scopri i programmi per il tuo paese e settore (indicativo, non uno sconto)" },
  da: { save: "Muligvis op til", with: "via", learn: "Se alle programmer", hint: "Muligt tilskud", generic_title: "Muligt tilskud tilgængeligt", generic_body: "Se programmer for dit land og sektor (vejledende, ikke en rabat)" },
};

/**
 * SubsidyHint, small inline badge showing the best subsidy for a given investment.
 * Reads country from localStorage (set on /subsidies page), falls back to NL.
 * Renders nothing when no subsidy beats threshold.
 *
 * Props:
 *  - amount (number): investment amount in EUR
 *  - variant: "card" (compact, for QuickstartCard) | "banner" (wider, for Cart total)
 */
export default function SubsidyHint({ amount, variant = "card" }) {
  const { lang } = useApp();
  const tt = T[lang] || T.en;
  const [best, setBest] = useState(null);

  useEffect(() => {
    if (!amount || amount < 1000) { setBest(null); return; }
    const country = localStorage.getItem("kc_subsidy_country") || "NL";
    const companySize = localStorage.getItem("kc_subsidy_size") || "small";
    api.get("/subsidies", { params: { country, company_size: companySize, min_investment: amount } })
      .then((r) => {
        const subs = r.data || [];
        const winner = subs
          .filter((s) => (s.expected_savings_eur || 0) > 0)
          .sort((a, b) => (b.expected_savings_eur || 0) - (a.expected_savings_eur || 0))[0];
        setBest(winner || null);
      })
      .catch(() => setBest(null));
  }, [amount, lang]);

  if (!best) {
    // Generic fallback for the banner variant, still drives traffic to /subsidies
    // even when no specific scheme matches the small investment.
    if (variant === "banner") {
      return (
        <Link
          to="/subsidies"
          data-testid="subsidy-hint-banner"
          className="subsidy-hint-banner block rounded-xl p-4 transition group"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <div className="subsidy-hint-icon w-10 h-10 rounded-full flex items-center justify-center shrink-0">
              <Banknote className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="subsidy-hint-eyebrow text-[10px] uppercase tracking-wider font-semibold">{tt.generic_title}</div>
              <div className="subsidy-hint-body text-sm sm:text-base mt-0.5">{tt.generic_body}</div>
            </div>
            <span className="subsidy-hint-cta text-xs font-semibold inline-flex items-center gap-1 shrink-0 group-hover:translate-x-1 transition">
              {tt.learn} <ArrowRight className="w-3 h-3" />
            </span>
          </div>
        </Link>
      );
    }
    return null;
  }

  const name = tr(best.name_i18n, lang) || best.name_i18n?.en || best.name_i18n?.nl;
  const savings = `€${Math.round(best.expected_savings_eur).toLocaleString(lang === "nl" ? "nl-NL" : "en-US")}`;
  const country = best.country_code === "EU" ? "🇪🇺" : flagFor(best.country_code);

  if (variant === "banner") {
    return (
      <Link
        to="/subsidies"
        data-testid="subsidy-hint-banner"
        className="subsidy-hint-banner block rounded-xl p-4 transition group"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <div className="subsidy-hint-icon w-10 h-10 rounded-full flex items-center justify-center shrink-0">
            <Banknote className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="subsidy-hint-eyebrow text-[10px] uppercase tracking-wider font-semibold">{tt.hint} {country}</div>
            <div className="subsidy-hint-body text-sm sm:text-base mt-0.5">
              {tt.save} <span className="subsidy-hint-amount font-semibold">{savings}</span> {tt.with} <span className="font-semibold">{name}</span>
            </div>
          </div>
          <span className="subsidy-hint-cta text-xs font-semibold inline-flex items-center gap-1 shrink-0 group-hover:translate-x-1 transition">
            {tt.learn} <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </Link>
    );
  }

  // card variant, compact pill
  return (
    <Link
      to="/subsidies"
      data-testid="subsidy-hint-card"
      className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold bg-[#7FD8B8]/15 border border-[#7FD8B8]/30 text-[#7FD8B8] hover:bg-[#7FD8B8]/25 transition"
      onClick={(e) => e.stopPropagation()}
    >
      <Banknote className="w-3 h-3" /> {tt.save} {savings} {country}
    </Link>
  );
}

function flagFor(code) {
  const m = { NL: "🇳🇱", BE: "🇧🇪", DE: "🇩🇪", FR: "🇫🇷", AT: "🇦🇹", ES: "🇪🇸", IT: "🇮🇹", DK: "🇩🇰", EU: "🇪🇺" };
  return m[code] || "";
}
